import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, ConfigProvider, Space, Tabs, Typography, message, theme as antdTheme } from 'antd';
import {
  space,
  ADMIN_HEADER_HEIGHT,
  useAdminContentPadding,
  useNarrowViewport,
} from '@viq-admin/ui';
import { EventRail } from './EventRail';
import { EventHeader } from './EventHeader';
import { KpiTiles } from './KpiTiles';
import { VolumeCoverageStrip } from './VolumeCoverageStrip';
import { BatchTable } from './BatchTable';
import { BulkActionsBar } from './BulkActionsBar';
import { BatchFooter } from './BatchFooter';
import { ExportDialog } from './ExportDialog';
import { SalesforceExportDialog } from './SalesforceExportDialog';
import { MoveLeadsDialog } from './MoveLeadsDialog';
import { CrmSourceControl } from './crm/CrmSourceControl';
import { CrmViews, type CrmCounts, type CrmSourceContext } from './crm/CrmViews';
import { CRM_COPY } from './crm/copy';
import { CRM_GRID_COPY } from './crm/crmGridCopy';
import { HelpHint, HintLine } from './crm/HelpHint';
import { defaultConferenceId, groupOf } from './eventGrouping';
import { notesTileState } from '../leadRules/notesTile';
import { useLeadRowSync } from './useLeadRowSync';
import { useLeadNoteSync } from './useLeadNoteSync';
import { useGridLeadRows } from './useGridLeadRows';
import type {
  BatchAggregationsFetcher,
  BatchBulkApi,
  BatchConference,
  BatchExportApi,
  BatchLead,
  BatchLeadFieldsApi,
  BatchLeadNotesApi,
  BatchLeadsFetcher,
  BatchMoveApi,
  CrmQueueApi,
  CrossTenantSalesforceApi,
  BatchTagsApi,
} from './types';
import { CRM_STAGE_STRIP_HEIGHT } from './types';

type CrmTab = 'leads' | 'clean' | 'match' | 'send';

/** What a conference reads as before its OWN counts arrive — including the
 *  moment after a switch, where the previous event's numbers would otherwise
 *  stand. `match`/`send` are `null` — not known — so their tabs render bare
 *  rather than claiming a zero nobody has counted yet. */
const ZERO_COUNTS: CrmCounts = { clean: 0, match: null, send: null, badge: 0 };
/** The unified grid's equivalent (port plan D9): Clean waits on the lead rows
 *  too, so before its own counts arrive it is not known either. */
const GRID_ZERO_COUNTS: CrmCounts = { clean: null, match: null, send: null, badge: 0 };

/**
 * A stage tab's label. A `null` count means "not known yet", and an unknown
 * count is shown as no count at all — `Send (0)` before anything has looked
 * would be a claim the screen has not earned: it reads as "nothing to do
 * here", which is the one thing it must never say on a queue nobody has
 * counted. A `0` that WAS counted is a real answer and shows as `(0)`.
 *
 * In practice only `send` arrives `null` for long: it waits on the live CRM
 * read that opening Match or Send triggers, because readiness turns on owner
 * data the cheap opening check never fetches. Clean and Match both come off
 * that check, so they carry numbers from the moment it lands.
 */
function stageLabel(name: string, count: number | null): string {
  return count === null ? name : `${name} (${count})`;
}

export interface BatchScreenProps {
  /** Conferences to choose from — already scoped by the shell (all tenants for
   *  the console; the caller's company for the customer portal). */
  conferences: BatchConference[];
  /** Fetch KPI + coverage aggregations for the selected conference. */
  fetchAggregations: BatchAggregationsFetcher;
  /** Fetch a cursor-paginated page of leads. */
  fetchLeads: BatchLeadsFetcher;
  /** Shell-provided tag + assignment API powering the editable Batch cells. */
  tagsApi: BatchTagsApi;
  /** Renders the lead-detail body inside the drawer (each shell's own). */
  renderLeadDetail: (leadId: string) => ReactNode;
  /** Shell-provided CSV export API powering the footer's Export dialog. */
  exportApi: BatchExportApi;
  /**
   * Shell-provided, CROSS-TENANT Salesforce export API. OPTIONAL — omitting it
   * hides the Salesforce affordance entirely. Wired by the super-admin console
   * only (target-company picker + type-CONFIRM gate); the customer portal's
   * former own-company variant was retired in p3 Task 6, superseded by
   * `crmApi` (the CRM merge queue's Clean/Match/Send views).
   */
  salesforceApi?: CrossTenantSalesforceApi;
  /**
   * Shell-provided CRM merge-queue API (p3 Clean/Match/Send, ADR 019). OPTIONAL
   * — omitting it (or an empty `connectedSources`) hides the stage tabs +
   * Clean/Match/Send views AND the event-rail badge entirely; the screen is
   * byte-identical to a shell with no CRM integration. Wired by the customer
   * portal only — the console has no own-company CRM connection.
   */
  crmApi?: CrmQueueApi;
  /**
   * Injected by the console to enable moving mis-scanned leads between
   * conferences. OPTIONAL — omitting it hides the affordance entirely, which
   * is what keeps this console-only: the customer portal never passes it.
   */
  moveApi?: BatchMoveApi;
  /**
   * Injected by the customer portal to give the leads table's row selection
   * something to do — tag / assign / delete the selected leads, restore them
   * on the Deleted tab. OPTIONAL — omitting it hides the affordance entirely,
   * checkbox column included; the console never passes it.
   */
  bulkApi?: BatchBulkApi;
  conferencesLoading?: boolean;
  /**
   * URL-controlled selection (customer portal deep-linking). When provided
   * (even as `null`), selection becomes CONTROLLED: the parent owns the value
   * and the auto-default, and the internal default-select effect is skipped.
   * When omitted (console), selection stays internal/uncontrolled — unchanged.
   */
  selectedId?: string | null;
  /** Fired when an event is chosen in the rail. In controlled mode the parent
   *  is expected to reflect the change back through `selectedId`. */
  onSelect?: (id: string) => void;
  /**
   * Stakeholder single-pane mode (customer portal). Renders NO rail: a
   * "Your Leads" title + a read-only table over ALL the caller's leads +
   * a disabled, export-less footer. Console never sets it.
   */
  singlePane?: boolean;
  /**
   * Renders per-row actions on the Deleted tab (the portal's rep-restore
   * button). Forwarded to BatchTable. Console omits it → no actions column.
   */
  renderDeletedRowActions?: (lead: BatchLead, reload: () => void) => ReactNode;
  /**
   * Read-only mode for the FULL layout (super-admin console readonly role):
   * table cells render without editors and the footer's Export/Salesforce
   * buttons are withheld. Distinct from `singlePane` (a portal layout).
   * Default: writable. Portal admins omit it → unchanged.
   */
  readOnly?: boolean;
  /**
   * Whether the export dialog offers the discovery toggles (Discovery answers
   * + Discovery Summary). Default `true` (customer portal).
   *
   * The console offers the switches only where it can actually honor them:
   * it gates this on its own company scope (`Boolean(companyId)`), because
   * the backend accepts `includeDiscovery` / `includeDiscoverySummary`
   * exactly when a `companyId` accompanies them, and 422s them otherwise
   * (cross-tenant "All companies" exports have no single company's discovery
   * columns to include). Company-scoped console exports get the same
   * switches as the customer portal; unscoped ones do not.
   *
   * This hides the switches; it does NOT pin the underlying state, which the
   * dialog still seeds from `exportApi.getColumnPrefs()`. The console shell
   * therefore ALSO refuses to send those params when unscoped — see its
   * `downloadExport`. The two guards are deliberately redundant.
   */
  showDiscovery?: boolean;
  /**
   * Threaded to the export dialog's `note` — shell-supplied context line.
   * The console sets this when its scope is "All companies," to explain why
   * the discovery toggles (and columns) are absent — the same `companyId`
   * gate as `showDiscovery`.
   */
  exportNote?: string;
  /**
   * Inline editing of the leads table's Name, Company, Title, Email, Phone and
   * Temp (spec 2026-09-17, viq-admin#54). OPTIONAL — omitting it leaves those
   * cells exactly as they are, and so do rows without `firstName` /
   * `lastName` (a backend without the route, viq-backend#117;
   * `rowsCarryLeadFields`). Independent of `readOnly` (spec D10): the
   * console passes it for `update Lead`, which its read-only role has, while
   * `readOnly` keeps tags and assignment read-only. Never used by the
   * stakeholder `singlePane`.
   */
  leadFieldsApi?: BatchLeadFieldsApi;
  /**
   * Editing the leads table's Notes column (the latest rep note, port plan
   * step 5). OPTIONAL — omitting it leaves the column read-only. Independent
   * of `readOnly`, like `leadFieldsApi`: the portal passes it for
   * `manage:Lead`, the console for `update Lead`. Never used by the
   * stakeholder `singlePane`.
   */
  leadNotesApi?: BatchLeadNotesApi;
  /**
   * The unified CRM grid (port plan step 6): the Clean, Match and Send tabs
   * reuse the leads grid — an "In your CRM" status column, the tab numbers
   * counting the work waiting on each tab (D9), Send ticking rows in the grid
   * with its button in the footer beside Export CSV. OPTIONAL, and only
   * meaningful where the CRM tabs show at all (a connected CRM, D4). Passed by
   * the portal's company-admin branch only; the stakeholder single-pane and
   * the console never pass it, and without it Clean / Match / Send keep their
   * own layouts exactly as before.
   */
  unifiedCrmGrid?: boolean;
}

/**
 * Shared, PARAMETERIZED Batch workspace (ADR 016). A two-panel shell with an
 * event-selection rail (left) and, for the selected event, the state-machine
 * pipeline + KPI tiles + volume & coverage strip + a cursor-paginated leads
 * table with editable tags/assignment (right). Clicking a lead opens the shared
 * Lead Detail drawer. Uses antd theme tokens (+ the shared brand theme for pill
 * colors) so light/dark is automatic.
 *
 * The console consumes it with internal (uncontrolled) selection, no single-pane
 * mode, the cross-tenant Salesforce variant, and the move-leads affordance
 * (`moveApi`). The customer portal additionally drives URL-controlled selection
 * (`selectedId`/`onSelect`), the stakeholder `singlePane` fallback, the CRM
 * merge-queue variant (`crmApi`), and the leads table's bulk actions
 * (`bulkApi`).
 */
export function BatchScreen({
  conferences,
  fetchAggregations,
  fetchLeads,
  tagsApi,
  renderLeadDetail,
  exportApi,
  salesforceApi,
  crmApi,
  moveApi,
  bulkApi,
  conferencesLoading,
  selectedId,
  onSelect,
  singlePane,
  readOnly,
  renderDeletedRowActions,
  showDiscovery,
  exportNote,
  leadFieldsApi,
  leadNotesApi,
  unifiedCrmGrid,
}: BatchScreenProps) {
  const { token } = antdTheme.useToken();

  // One ref: the single-pane and rail layouts are mutually exclusive returns,
  // so only one of the two scroll containers is mounted at a time.
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // ── Selection: controlled/uncontrolled hybrid ──────────────────────────────
  // `selectedId !== undefined` → the parent owns selection + defaulting (URL
  // deep-linking); the internal default-select effect is skipped. Otherwise the
  // selection lives in local state and auto-defaults to the most-recent active
  // event once the list loads.
  const isControlled = selectedId !== undefined;
  const [internalSelectedId, setInternalSelectedId] = useState<string | null>(null);
  const effectiveSelectedId = isControlled ? selectedId : internalSelectedId;

  const [exportOpen, setExportOpen] = useState(false);
  const [sfExportOpen, setSfExportOpen] = useState(false);

  // ── Move leads (console-only) — dialog + per-row selection ──────────────
  const [moveOpen, setMoveOpen] = useState(false);
  /** Bumped after a successful move or bulk run so BatchTable re-fetches. */
  const [reloadToken, setReloadToken] = useState(0);
  /** Bumped when the lead drawer closes: BatchTable re-fetches the rows it
   *  has loaded (every page of them), keeping sort and filters. */
  const [refreshToken, setRefreshToken] = useState(0);
  const [selectedPersonIds, setSelectedPersonIds] = useState<string[]>([]);
  /** Mirrors BatchTable's Active/Deleted tab — the bulk bar's action set. */
  const [leadsTab, setLeadsTab] = useState<'active' | 'deleted'>('active');

  // ── CRM merge-queue stage tabs (p3 Task 6) ──────────────────────────────
  const [crmTab, setCrmTab] = useState<CrmTab>('leads');
  // The unified grid's Send button goes here, beside Export CSV.
  const [footerActionSlot, setFooterActionSlot] = useState<HTMLElement | null>(null);
  /**
   * The counts `CrmViews` last reported, STAMPED with the conference they were
   * computed for. `shownCounts` below reads them back only while that stamp
   * still matches the selection — that is what keeps one event's numbers off
   * another event's stage tabs and rail badge.
   *
   * Clearing them from the conference-change effect instead cannot do it, and
   * that is the bug this shape replaces: `CrmViews` is keyed by conference, so
   * a switch remounts it, and React flushes a child's mount effect BEFORE the
   * parent's. Against a warm react-query cache (`staleTime: Infinity`) the
   * child has data on its first render, reports the new event's real counts
   * from that mount effect, and the parent's reset then lands last in the same
   * batch — leaving `Clean (0) Match (0) Send (0)` and no badge until a full
   * page reload rebuilt the tree.
   */
  const [crmCounts, setCrmCounts] = useState<{ conferenceId: string | null; counts: CrmCounts }>({
    conferenceId: null,
    counts: ZERO_COUNTS,
  });
  /** `CrmViews`' manual re-check, published on its mount so the stage toolbar
   *  below can render the Refresh control in the tab bar's extra slot. */
  const crmRefreshRef = useRef<(() => void) | null>(null);
  /** `CrmViews`' match-check-only re-run (no live preview), published on its
   *  mount: what an inline edit burst re-runs. */
  const crmMatchRecheckRef = useRef<((editEpoch?: number) => void) | null>(null);
  /** Grid mode: `CrmViews`' "the leads changed" mark for its live read (no fetch). */
  const crmMarkStaleRef = useRef<(() => void) | null>(null);
  // Stored by value, not as the callback object itself: CrmViews republishes on
  // every scope change, and holding the object would re-render on identity
  // alone. The queue stays CrmViews' — this is a render-only copy.
  //
  // STAMPED with the conference it was published for, same shape and same
  // reason as `crmCounts` above: `CrmViews` is keyed by conference, so a
  // switch remounts it at `connectedSources[0]` while the toolbar would
  // otherwise go on painting the previous event's scope until the new
  // mount's publish effect fires. `shownSource` below reads this back only
  // while the stamp still matches the selection, closing that one-frame
  // window (and the click that could land in it and dispatch into the
  // unmounted reducer).
  const [crmSource, setCrmSource] = useState<{
    conferenceId: string | null;
    ctx: CrmSourceContext | null;
  }>({ conferenceId: null, ctx: null });
  // Reset to the Leads tab on conference change — a stale Clean/Match/Send
  // selection from the PREVIOUS event is a confusing default for a new one.
  //
  // Adjusted DURING render, not from an effect: `CrmViews` is keyed by
  // conference, so a switch remounts it, and an effect lands after that mount
  // has already read `view`. A mount on 'match'/'send' latches its live CRM
  // read — so an effect here fires the expensive lookup against the new event
  // before dropping the operator on Leads, which is exactly what holding that
  // read until Match or Send is opened exists to prevent.
  const [crmTabConferenceId, setCrmTabConferenceId] = useState<string | null>(effectiveSelectedId);
  if (crmTabConferenceId !== effectiveSelectedId) {
    setCrmTabConferenceId(effectiveSelectedId);
    setCrmTab('leads');
  }

  const narrow = useNarrowViewport();
  const contentPadding = useAdminContentPadding();
  const [railCollapsed, setRailCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('viq-admin.batch.railCollapsed') === '1';
  });
  // On a phone the rail floats over the page, so it starts closed (an event is
  // picked by default) and closes again once one is picked. That is a
  // transient menu, not a layout preference, so it isn't remembered — and the
  // desktop choice above is left alone.
  const [narrowRailOpen, setNarrowRailOpen] = useState(false);
  function toggleRail() {
    if (narrow) {
      setNarrowRailOpen((prev) => !prev);
      return;
    }
    setRailCollapsed((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('viq-admin.batch.railCollapsed', next ? '1' : '0');
      }
      return next;
    });
  }

  // Default to the most-recent active event once the list loads (fallback:
  // most-recent overall). Uncontrolled only — the controlled parent defaults.
  useEffect(() => {
    if (isControlled) return;
    if (!internalSelectedId && conferences.length) {
      setInternalSelectedId(defaultConferenceId(conferences));
    }
  }, [isControlled, internalSelectedId, conferences]);

  function handleSelect(id: string) {
    if (!isControlled) setInternalSelectedId(id);
    onSelect?.(id);
    setNarrowRailOpen(false);
  }

  const selected = useMemo(
    () => conferences.find((c) => c.id === effectiveSelectedId) ?? null,
    [conferences, effectiveSelectedId],
  );
  const selectedGroup = useMemo(
    () => (selected ? groupOf(selected) : 'past'),
    [selected],
  );

  // Zero until the CURRENTLY selected event's own counts arrive. Derived
  // during render, so there is no frame in which the previous event's numbers
  // appear under a new one — and no race with the child's report.
  const gridRequested = !!unifiedCrmGrid;
  const shownCounts =
    selected && crmCounts.conferenceId === selected.id
      ? crmCounts.counts
      : gridRequested
        ? GRID_ZERO_COUNTS
        : ZERO_COUNTS;
  // Same guard as `shownCounts`, for the same reason: don't paint a CRM label
  // published for a conference that is no longer selected.
  const shownSource =
    selected && crmSource.conferenceId === selected.id ? crmSource.ctx : null;

  const queryClient = useQueryClient();
  const aggsQuery = useQuery({
    queryKey: ['batchAggregations', selected?.id],
    queryFn: () => fetchAggregations(selected!.id),
    enabled: !!selected?.id,
  });
  const aggs = aggsQuery.data;

  // salesforceApi is CROSS-TENANT ONLY now (the console) — the portal's former
  // own-company variant was retired in p3 Task 6 in favor of `crmApi`.
  const crossTenantSf = salesforceApi ?? null;
  const crossTenantConnected = !!crossTenantSf?.connected;

  // ── CRM merge queue gate (p3 Task 6) ────────────────────────────────────
  // Stage tabs + Clean/Match/Send + the rail badge only when there's an actual
  // connected source to check against; a shell that never wires `crmApi` (or
  // whose caller has no connected source) renders byte-identically to before.
  const crmTabsEnabled = !!(
    crmApi &&
    crmApi.connectedSources.length > 0 &&
    selected &&
    !singlePane &&
    !readOnly
  );
  // The unified grid lives inside the CRM tabs, so it takes their gate as is.
  const gridMode = gridRequested && crmTabsEnabled;

  // ── Assigned-to suggestions (spec 2026-10-02 §5.1) ──────────────────────
  // One roster query per CONNECTED source, sharing the Match view's
  // ['crmOwners', source] cache key so opening Match later costs no second
  // request. Failures are swallowed into "no suggestions": a plain input is
  // the pre-feature behavior, and a user who never had suggestions should not
  // learn the feature exists through an error.
  //
  // Gated the same shape as `crmTabsEnabled` below (connected source,
  // !singlePane, !readOnly), plus `canManage`: the owners endpoint is
  // company_admin-only, so a caller without that permission would just draw
  // every roster fetch down in a 403 for nothing.
  const suggestionSources =
    crmApi && crmApi.canManage && !readOnly && !singlePane ? crmApi.connectedSources : [];
  const ownerRosters = useQueries({
    queries: suggestionSources.map((source) => ({
      queryKey: ['crmOwners', source],
      queryFn: () => crmApi!.fetchOwners(source),
      staleTime: 10 * 60_000,
      retry: false,
    })),
  });
  // Referential stability matters: every AssignmentCell memoizes its options
  // on this array, and the table renders one cell per row with no pagination.
  // `ownerRosters` is a fresh array each render, so key on the data refs.
  const rosterKey = ownerRosters.map((q) => q.dataUpdatedAt).join(',');
  const assigneeSuggestions = useMemo<string[] | undefined>(() => {
    const names = new Set<string>();
    for (const q of ownerRosters) {
      for (const o of q.data ?? []) {
        const n = o.name.trim();
        if (n !== '') names.add(n);
      }
    }
    if (names.size === 0) return undefined;
    return Array.from(names).sort((a, b) => a.localeCompare(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rosterKey]);

  // ── Move leads (console footer, or portal bulk bar) ───────────────────────
  // Available whenever the shell wires `moveApi` (and we're not read-only).
  // NOT gated on `moveApi.companyId` — a super-admin with "All tenants"
  // selected still gets the affordance, just disabled with an explanation
  // (see `moveDisabledReason` below), rather than a vanishing button.
  const moveAvailable = !!moveApi && !readOnly;

  // ── Bulk lead actions (portal) ─────────────────────────────────────────────
  // The portal's other reason to have checkboxes. Unlike `moveAvailable` this
  // ALSO requires `canManage`: a selection whose every action is denied is
  // worse than no selection, which is how the checkboxes came to look broken
  // in the first place.
  const bulkAvailable = !!bulkApi && bulkApi.canManage && !readOnly;

  // One selection serves both. The console wires `moveApi` alone (Move lives
  // in the footer); the portal wires `bulkApi` and, for companies with the
  // lead-move flag, `moveApi` too (Move joins the bulk bar). Sharing the state
  // shares the clearing guards below rather than duplicating them.
  const selectionEnabled = moveAvailable || bulkAvailable;

  // Drop any selection when the operator switches conference or company scope.
  //
  // Without this the checkboxes clear visually (the table reloads) while
  // `selectedPersonIds` still holds the PREVIOUS conference's lead ids, and the
  // move button stays enabled. The dialog would then show the new conference's
  // name while carrying the old conference's leads — and since the backend
  // validates only the leads' own company and the destination grant, a move
  // within the same tenant would SUCCEED, silently relocating leads the
  // operator never selected. Across tenants it fails with a misleading
  // "not granted" error that points at the wrong cause.
  //
  // The portal's bulk actions inherit the same guard, and need it just as
  // badly: a bulk tag or delete carried across a conference switch would
  // succeed outright — the leads are still the caller's own company's.
  //
  // The company scope only exists in the console ("Operate as"). The portal's
  // moveApi carries a constant marker that appears once the company-features
  // check loads, and keying the reset on it would clear a selection the
  // customer had just made. So the key is console-only.
  const scopeCompanyId = bulkAvailable ? null : (moveApi?.companyId ?? null);
  useEffect(() => {
    setSelectedPersonIds([]);
    setMoveOpen(false);
  }, [selected?.id, scopeCompanyId]);

  // The third thing that invalidates a selection: leaving the Leads stage.
  // That is the moment this screen UNMOUNTS the leads table, and a selection
  // outliving the table it came from is a bulk action aimed at rows nobody
  // can see — tick two rows on the Deleted tab, step through Clean, come
  // back, and the table is on Active while `selectedPersonIds` still holds
  // the deleted ids and the bar still offers Restore.
  //
  // BatchTable republishes its own fresh state when it remounts, which fixes
  // that a frame later — but a frame later is still a frame with the wrong
  // bar over the wrong rows, and the invariant belongs to whoever owns the
  // state. Clearing on the way OUT means it can never go stale in the first
  // place; the child's republish stays as the belt to this braces.
  useEffect(() => {
    if (crmTab === 'leads') return;
    setSelectedPersonIds([]);
    setLeadsTab('active');
  }, [crmTab]);

  // ── Inline lead editing (spec 2026-09-17 §2.2) ─────────────────────────
  // The sync store lives HERE, not in the table: the CRM stage tabs unmount
  // the table, and saves in flight must survive that. After a burst of saves
  // (debounced with the tiles' refresh) the CRM MATCH CHECK re-runs once when
  // its tabs are mounted, so Clean / Match follow an edit such as a new
  // email — the same re-run develop's Clean email fix does. Never the manual
  // Refresh: that also re-runs the live preview, a CRM lookup of every lead
  // with an email, which only the first Match / Send visit or Refresh may do.
  const crmBurstScopeRef = useRef<{ enabled: boolean; conferenceId: string | null }>({
    enabled: false,
    conferenceId: null,
  });
  crmBurstScopeRef.current = { enabled: crmTabsEnabled, conferenceId: selected?.id ?? null };
  const leadSync = useLeadRowSync({
    api: singlePane ? undefined : leadFieldsApi,
    onSavedBurst: (conferenceIds) => {
      const { enabled, conferenceId } = crmBurstScopeRef.current;
      // The store's epoch now: every save of this burst has confirmed, so a
      // re-check already issued since (grid Clean's own) covers them.
      if (enabled && conferenceId && conferenceIds.includes(conferenceId)) {
        crmMatchRecheckRef.current?.(leadSyncRef.current?.epoch());
      }
    },
  });
  const leadSyncRef = useRef(leadSync);
  leadSyncRef.current = leadSync;
  // The Notes column's saves, hosted here for the same reason: a note save
  // still out when the CRM tabs unmount the table must land in the table
  // that comes back — shown as saving while it is out, laid over the rows
  // that table loads once confirmed — never lost with the cell that started
  // it. No CRM re-check: a note changes nothing the match reads.
  const noteSync = useLeadNoteSync();

  // ── The unified CRM grid's lead rows (port plan step 6, F-F) ───────────
  // ONE load of every active lead at the event, for the tab numbers and
  // every lens alike; edits are written through, so it is re-read only when
  // something else may have changed the event's rows (`reloadGridRows`).
  const gridRows = useGridLeadRows({
    enabled: gridMode,
    conferenceId: selected?.id ?? null,
    fetchLeads,
    leadSync,
    noteSync,
  });
  const reloadGridRows = gridRows.reload;
  // A move, a bulk run (`reloadToken`) or a restore changes the event's
  // leads: in grid mode the rows are re-read, the cheap match check re-runs
  // (so the people the live read covers follow), and the live read is marked
  // stale — re-run on the next visit to Match or Send, never fetched now.
  const gridModeRef = useRef(gridMode);
  gridModeRef.current = gridMode;
  const leadsChanged = useCallback(() => {
    reloadGridRows();
    if (gridModeRef.current) crmMatchRecheckRef.current?.();
  }, [reloadGridRows]);
  const seenReloadTokenRef = useRef(reloadToken);
  useEffect(() => {
    if (seenReloadTokenRef.current === reloadToken) return;
    seenReloadTokenRef.current = reloadToken;
    leadsChanged();
  }, [reloadToken, leadsChanged]);
  /** A tag or assignment change from either table, into the shared rows.
   *  An assignment can change the CRM owner the live read suggests. */
  const patchGridRow = gridMode
    ? (leadId: string, patch: Partial<BatchLead>) => {
        gridRows.patchRow(leadId, (row) => ({ ...row, ...patch }));
        if ('assignedTo' in patch) crmMarkStaleRef.current?.();
      }
    : undefined;
  const deletedRowActions =
    renderDeletedRowActions && gridMode
      ? (lead: BatchLead, reload: () => void) =>
          renderDeletedRowActions(lead, () => {
            reload();
            leadsChanged();
          })
      : renderDeletedRowActions;

  const summaryText = selected ? `${selected.name}: viewing event data` : '';

  // ── Stakeholder single-pane fallback ───────────────────────────────────────
  if (singlePane) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 112px)' }}>
        <div ref={scrollContainerRef} style={{ flex: 1, overflowY: 'auto', padding: '0 16px' }}>
          <Typography.Title level={2}>Your Leads</Typography.Title>
          <Typography.Paragraph type="secondary">
            All leads visible to your account.
          </Typography.Paragraph>
          <BatchTable
            conferenceId={null}
            fetchLeads={fetchLeads}
            tagsApi={tagsApi}
            renderLeadDetail={renderLeadDetail}
            readOnly
            renderDeletedRowActions={renderDeletedRowActions}
            getScrollContainer={() => scrollContainerRef.current}
          />
        </div>
        <BatchFooter summaryText="" disabled />
      </div>
    );
  }

  // Whether the footer's Salesforce button is available at all. Fail-closed:
  // requires at least one connected tenant (`crossTenantConnected`, set by the
  // console from `listSalesforceCompanies()` length). Missing/false hides the
  // affordance rather than opening a dead-end dialog with no targets.
  const sfButtonAvailable = !!crossTenantSf && crossTenantConnected && !!selected;

  // The CRM source, Refresh and its hint: beside the stage tabs on a desktop,
  // beneath them on a phone.
  const crmToolbarControls = (
    <Space size={space.xs} align="center">
      {shownSource && (
        <CrmSourceControl
          sources={shownSource.sources}
          value={shownSource.value}
          canManage={shownSource.canManage}
          onRequestChange={shownSource.onRequestChange}
        />
      )}
      <Button size="small" onClick={() => crmRefreshRef.current?.()}>
        {CRM_COPY.refreshButton}
      </Button>
      {/* The answer to "is it calling Salesforce on every page load?" is
          now: only about people it has never looked up, or whose email
          changed — the full live read waits for Match or Send. The hint
          beside this control says both halves rather than claiming either
          extreme. */}
      <HelpHint
        {...(gridMode ? CRM_GRID_COPY.help.refresh : CRM_COPY.help.refresh)}
        testId="crm-help-refresh"
        placement="bottomRight"
      />
    </Space>
  );

  return (
    // Bigger checkboxes and radios for the worksheet and the CRM views
    // (gh-31 §4.2): antd sizes both off `controlInteractiveSize` (16 by
    // default), and at 16px on a dense table they disappeared. Scoped here,
    // not in the theme, so the rest of the app is unchanged.
    <ConfigProvider theme={{ token: { controlInteractiveSize: 20 } }}>
      {/* Edge to edge: the event rail is a full-height column flush against
          the IQ Panel and the header (it shares the IQ Panel's right edge as
          its left), and the footer runs the full width beneath like a status
          bar. AdminLayout pads every page, so cancel that padding here; the
          main column below restores it for its own content. */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          margin: -contentPadding,
          // `dvh` on a phone: `vh` there is the height with the browser's
          // toolbars hidden, which would push the footer under them.
          height: `calc(${narrow ? '100dvh' : '100vh'} - ${ADMIN_HEADER_HEIGHT}px)`,
        }}
      >
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <EventRail
            conferences={conferences}
            selectedId={effectiveSelectedId ?? null}
            onSelect={handleSelect}
            collapsed={narrow ? !narrowRailOpen : railCollapsed}
            onToggleCollapsed={toggleRail}
            overlay={narrow}
            badgeByConferenceId={
              crmTabsEnabled && selected ? { [selected.id]: shownCounts.badge } : undefined
            }
            // Grid mode's badge counts more terms (D9); its sentence says
            // exactly which, and only those already known.
            badgeTooltip={
              gridMode
                ? (n) => CRM_GRID_COPY.badgeTooltip(n, shownCounts.badgeIncludesLiveRead === true)
                : undefined
            }
          />
          <div
            ref={scrollContainerRef}
            data-testid="crm-scroll-container"
            style={{
              flex: 1,
              // Lets a wide table scroll inside this column instead of
              // widening it past the screen.
              minWidth: 0,
              overflowY: 'auto',
              // Keeps the page content exactly where it sat before the layout
              // went edge to edge. (Its left side is spaced by its children.)
              paddingRight: contentPadding,
            }}
          >
            {/* The top inset lives on THIS wrapper, not the scroll container
                above: the stage strip below sticks at `top: 0` relative to the
                scroll container's own scrollport, so a top padding on that
                container leaves a band the strip pins BELOW — scrolled
                content then paints above the strip. Moving the inset here
                keeps the scrollport's top flush with the strip's sticky
                offset while leaving the at-rest position (event header one
                page padding below the app header) unchanged, since
                this wrapper still opens the scroll container's content. */}
            <div style={{ paddingTop: contentPadding }}>
            {selected ? (
              <>
                <EventHeader conference={selected} group={selectedGroup} />
                <KpiTiles
                  totalScans={aggs?.kpi?.total_scans}
                  scansGoal={aggs?.kpi?.scans_goal}
                  totalNotes={aggs?.kpi?.total_notes}
                  totalTasks={aggs?.kpi?.total_tasks}
                  meetingsBooked={aggs?.kpi?.meetings_booked}
                  notesTone={notesTileState(aggs?.leadRules).tone}
                  notesCaption={notesTileState(aggs?.leadRules).caption}
                />
                <VolumeCoverageStrip
                  repeatVisitors={aggs?.coverage?.repeat_visitors}
                  vipCount={aggs?.coverage?.vip_count}
                  uniqueScans={aggs?.coverage?.unique_scans}
                  uniqueCompanies={aggs?.coverage?.unique_companies}
                />
                {crmTabsEnabled && (
                  // The stage strip is the primary navigation of the whole CRM
                  // flow, on the page's own `space.lg` gutter — the same left
                  // edge as the header and the two tile strips above.
                  //
                  // Card tabs rather than a `Segmented`: testers did not read
                  // the Segmented as something to click at all (it is an antd
                  // inline-block that rendered as a narrow control orphaned at
                  // an edge matching nothing), and in dark mode it sat at
                  // almost exactly its track's colour. Tabs carry the role and
                  // keyboard semantics the strip always meant to have — arrow
                  // keys move between stages, and `role="tab"` says what they
                  // are.
                  //
                  // No panes: `items` deliberately carry no `children`, because
                  // BatchScreen renders the stage body itself below this strip.
                  // Refresh and its hint ride in the tab bar's extra slot so the
                  // row stays one deliberate toolbar rather than two (on a
                  // desktop; see `tabBarExtraContent` below for a phone).
                  <div
                    data-testid="crm-tab-toolbar"
                    style={{
                      padding: `0 ${space.lg}px`,
                      // Sticks to the top of the SAME scroll container that
                      // `scrollContainerRef` marks — deliberately not a new
                      // scroll parent: that ref is handed to the leads table's
                      // `getScrollContainer` for infinite scroll, and moving it
                      // breaks paging silently.
                      position: 'sticky',
                      top: 0,
                      // Fix round 1: antd's own sticky Table header shares
                      // this same scroll container (BatchTable's `sticky`
                      // prop, anchored to `scrollContainerRef`) and paints at
                      // `zIndexTableFixed + 1 = 3` in antd 5.29.3. The real
                      // fix is `offsetHeader` on that Table (below) docking
                      // its header BELOW this strip instead of under it — but
                      // this zIndex also wins the strip the collision if the
                      // two ever do meet.
                      zIndex: 4,
                      // TRUTHFUL, not decorative: forces this div to actually
                      // BE `CRM_STAGE_STRIP_HEIGHT` tall, which is the same
                      // number `BatchTable`'s `offsetHeader` and `SendView`'s
                      // rail `top` are built from. Without this the strip's
                      // real height is whatever antd's Tabs happens to render
                      // at, and the other two sticky consumers below it have
                      // no way to know that number.
                      minHeight: CRM_STAGE_STRIP_HEIGHT,
                      // Opaque, or the stage body shows through as it scrolls
                      // under. colorBgLayout, not colorBgContainer: the
                      // scroll column itself carries no background (it sits
                      // directly on the app shell's page surface), and only
                      // elevated cards in this screen — MetricCard,
                      // BatchFooter, MatchView — opt into colorBgContainer.
                      // The strip isn't a card, so it must match the page.
                      background: token.colorBgLayout,
                    }}
                  >
                    <Tabs
                      type="card"
                      // Small on a phone, so all four stages fit across it.
                      size={narrow ? 'small' : 'large'}
                      activeKey={crmTab}
                      onChange={(k) => setCrmTab(k as CrmTab)}
                      items={[
                        { key: 'leads', label: CRM_COPY.stage.leads },
                        // Clean's count comes from the opening check, which always
                        // runs — so it is always a number. Match's and Send's
                        // wait on the read those tabs trigger. (In grid mode
                        // Clean also waits for the lead rows, D9.)
                        { key: 'clean', label: stageLabel(CRM_COPY.stage.clean, shownCounts.clean) },
                        { key: 'match', label: stageLabel(CRM_COPY.stage.match, shownCounts.match) },
                        { key: 'send', label: stageLabel(CRM_COPY.stage.send, shownCounts.send) },
                      ]}
                      // On a phone the four stages need the whole strip, or
                      // antd folds them into an overflow menu; the controls
                      // drop to a row of their own beneath it instead.
                      tabBarExtraContent={narrow ? undefined : { right: crmToolbarControls }}
                    />
                  </div>
                )}
                {crmTabsEnabled && narrow && (
                  <div
                    style={{
                      // Contains HelpHint's visually hidden text, which is
                      // absolutely positioned: with no positioned ancestor
                      // short of the page it escapes this scroll column and
                      // makes the whole page scroll. (Beside the tabs on a
                      // desktop, the tab bar contains it.)
                      position: 'relative',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      padding: `${space.sm}px ${space.lg}px 0`,
                    }}
                  >
                    {crmToolbarControls}
                  </div>
                )}
                {/* Scoped to the CRM-enabled Leads tab: the super-admin console
                    never renders the stage tabs, so its plain table is untouched
                    and BatchScreen grows no CRM prose of its own. */}
                {crmTabsEnabled && crmTab === 'leads' && (
                  <div style={{ padding: `0 ${space.lg}px` }}>
                    <HintLine text={CRM_COPY.help.tabLeads.text} testId="crm-hint-tab-leads" />
                  </div>
                )}
                {(!crmTabsEnabled || crmTab === 'leads') && (
                  <>
                    {bulkAvailable && bulkApi && (
                      <BulkActionsBar
                        bulkApi={bulkApi}
                        tagsApi={tagsApi}
                        tab={leadsTab}
                        suggestions={assigneeSuggestions}
                        selectedIds={selectedPersonIds}
                        onMove={moveAvailable && moveApi?.companyId ? () => setMoveOpen(true) : undefined}
                        onClearSelection={() => setSelectedPersonIds([])}
                        onCompleted={() => {
                          // Same reasoning as the post-move refresh below: the
                          // rows on screen no longer match the server, and
                          // BatchTable's list is plain state with no query key
                          // to invalidate.
                          setSelectedPersonIds([]);
                          setReloadToken((t) => t + 1);
                        }}
                      />
                    )}
                    <BatchTable
                      conferenceId={selected.id}
                      fetchLeads={fetchLeads}
                      tagsApi={tagsApi}
                      reloadToken={reloadToken}
                      readOnly={readOnly}
                      renderLeadDetail={renderLeadDetail}
                      renderDeletedRowActions={deletedRowActions}
                      assigneeSuggestions={assigneeSuggestions}
                      // The Scans tile's count: the event's non-deleted leads,
                      // which is what the Active tab lists — the "of 312" in
                      // the table's loaded-rows cue. The table ignores it on
                      // the Deleted tab.
                      activeLeadsTotal={aggs?.kpi?.total_scans}
                      selectable={selectionEnabled}
                      selectedLeadIds={selectionEnabled ? selectedPersonIds : undefined}
                      onSelectedLeadIdsChange={
                        selectionEnabled ? setSelectedPersonIds : undefined
                      }
                      onTabChange={bulkAvailable ? setLeadsTab : undefined}
                      refreshToken={refreshToken}
                      leadFieldsApi={leadFieldsApi}
                      leadRowSync={leadSync}
                      leadNotesApi={leadNotesApi}
                      leadNoteSync={noteSync}
                      onLeadRowPatched={patchGridRow}
                      getScrollContainer={() => scrollContainerRef.current}
                      // Fix round 1: the `crm-tab-toolbar` strip only renders
                      // when `crmTabsEnabled` — this table's `!crmTabsEnabled`
                      // branch has no strip above it at all, so it must stay
                      // un-offset. When the strip IS present, its own sticky
                      // `top: 0` claims the same row antd's sticky header
                      // wants, so the header needs to dock below it.
                      offsetHeader={crmTabsEnabled ? CRM_STAGE_STRIP_HEIGHT : 0}
                      onLeadDetailClose={() => {
                        // Whatever the operator just did behind that drawer —
                        // most often adding notes until a company lead-rules
                        // requirement was met — the row's red flag and the
                        // Total Notes tile are both computed server-side, so
                        // without this they go on showing the pre-edit state
                        // until an unrelated reload happens to catch them up.
                        //
                        // The table's list is plain component state, no
                        // query key to invalidate, so a token re-fetches it:
                        // `refreshToken`, not the post-move/bulk
                        // `reloadToken`, because "+N earlier notes" makes
                        // this drawer common and a first-page reload would
                        // drop every loaded page and reshuffle a sort over
                        // them. The refresh brings back as many rows as were
                        // loaded. The aggregations tile IS a react-query
                        // query, so it gets invalidated instead, exactly like
                        // the other invalidateQueries call sites in this
                        // package (DiscoveryPanel, CrmViews).
                        setRefreshToken((t) => t + 1);
                        void queryClient.invalidateQueries({
                          queryKey: ['batchAggregations', selected.id],
                        });
                        // Grid mode: the CRM tabs' rows too, once.
                        reloadGridRows();
                      }}
                    />
                  </>
                )}
                {crmTabsEnabled && crmApi && (
                  <CrmViews
                    key={selected.id}
                    conferenceId={selected.id}
                    api={crmApi}
                    view={crmTab}
                    // Inline by design: CrmViews' report effect deliberately
                    // omits `onCounts` from its deps, so this closure's identity
                    // never re-fires it. Adding it there would loop against the
                    // setState here.
                    onCounts={(counts) => setCrmCounts({ conferenceId: selected.id, counts })}
                    // Keyed by conference above, so a switch remounts CrmViews
                    // and re-publishes — the toolbar's Refresh never holds a
                    // handle onto the previous event's queue.
                    onRefreshReady={(refresh) => {
                      crmRefreshRef.current = refresh;
                    }}
                    onRecheckReady={(recheck) => {
                      crmMatchRecheckRef.current = recheck;
                    }}
                    onPreviewStaleReady={(markStale) => {
                      crmMarkStaleRef.current = markStale;
                    }}
                    // Inline for the same reason as `onCounts` above: stamped
                    // with the conference it was published for, so a switch
                    // in-flight can't have this land under the newly
                    // selected event.
                    onSourceContext={(ctx) => setCrmSource({ conferenceId: selected.id, ctx })}
                    // The unified CRM grid (port plan step 6): Clean / Match /
                    // Send as lenses on this screen's own leads grid, with the
                    // same props the Leads tab's table gets.
                    grid={
                      gridMode
                        ? {
                            rows: gridRows,
                            actionSlot: footerActionSlot,
                            leadSync,
                            noteSync,
                            renderGrid: (lens) => (
                              // Its rows are the lens's (the screen's one
                              // load), so it takes no reload or refresh token.
                              <BatchTable
                                lens={lens}
                                conferenceId={selected.id}
                                fetchLeads={fetchLeads}
                                tagsApi={tagsApi}
                                readOnly={readOnly}
                                renderLeadDetail={renderLeadDetail}
                                assigneeSuggestions={assigneeSuggestions}
                                leadFieldsApi={leadFieldsApi}
                                leadRowSync={leadSync}
                                leadNotesApi={leadNotesApi}
                                leadNoteSync={noteSync}
                                onLeadRowPatched={patchGridRow}
                                getScrollContainer={() => scrollContainerRef.current}
                                offsetHeader={CRM_STAGE_STRIP_HEIGHT}
                                onLeadDetailClose={() => {
                                  // As on the Leads tab: the tiles, and the
                                  // event's rows, once. (The lens re-checks
                                  // the CRM itself, the cheap way.)
                                  void queryClient.invalidateQueries({
                                    queryKey: ['batchAggregations', selected.id],
                                  });
                                  reloadGridRows();
                                }}
                              />
                            ),
                          }
                        : undefined
                    }
                  />
                )}
              </>
            ) : (
              <div style={{ padding: space.xxl, textAlign: 'center', color: token.colorTextTertiary }}>
                {conferencesLoading ? 'Loading events…' : 'Pick an event from the left to begin.'}
              </div>
            )}
            </div>
          </div>
        </div>
        <BatchFooter
          summaryText={summaryText}
          onExport={selected && !readOnly ? () => setExportOpen(true) : undefined}
          onExportSalesforce={
            sfButtonAvailable && !readOnly ? () => setSfExportOpen(true) : undefined
          }
          onMove={
            moveAvailable && !bulkAvailable && selectedPersonIds.length > 0
              ? () => setMoveOpen(true)
              : undefined
          }
          moveDisabledReason={
            !bulkAvailable && !moveApi?.companyId ? 'To move leads, choose a company instead of All tenants.' : undefined
          }
          disabled={!selected}
          // Only the unified grid's Send tab puts an action here.
          actionSlotRef={gridMode ? setFooterActionSlot : undefined}
          hasPrimaryAction={gridMode && crmTab === 'send'}
        />
        {selected && (
          <ExportDialog
            open={exportOpen}
            conferenceId={selected.id}
            conferenceName={selected.name}
            exportApi={exportApi}
            onClose={() => setExportOpen(false)}
            showDiscovery={showDiscovery}
            note={exportNote}
          />
        )}
        {crossTenantSf && selected && (
          <SalesforceExportDialog
            open={sfExportOpen}
            conferenceId={selected.id}
            conferenceName={selected.name}
            salesforceApi={crossTenantSf}
            onClose={() => setSfExportOpen(false)}
          />
        )}
        {moveApi?.companyId && selected && (
          <MoveLeadsDialog
            open={moveOpen}
            api={moveApi}
            leadIds={selectedPersonIds}
            currentConferenceId={selected.id}
            currentConferenceName={selected.name}
            onClose={() => setMoveOpen(false)}
            onMoved={(result) => {
              // Confirm out loud, matching the Salesforce dialogs' toast. Without
              // this the dialog just closes and the rows vanish, which reads as
              // "nothing happened" — the moved leads have legitimately left this
              // conference's view. Every moved lead now gets a note, so
              // notesWritten normally equals moved; it is still read from the
              // response rather than assumed.
              const notes = result.notesWritten
                ? `, with ${result.notesWritten} ${result.notesWritten === 1 ? 'note' : 'notes'} recording the move`
                : '';
              // Carried-over answers are called out explicitly: the operator
              // needs to know the answers were kept, not silently dropped.
              const carried = result.discoveryAnswersCarriedOver
                ? `. ${result.discoveryAnswersCarriedOver} discovery ${result.discoveryAnswersCarriedOver === 1 ? 'answer' : 'answers'} stayed with the original event`
                : '';
              void message.success(
                `Moved ${result.moved} ${result.moved === 1 ? 'lead' : 'leads'} to another event${notes}${carried}.`,
              );
              // Clear the selection and force the table to re-fetch. The moved
              // leads now belong to another conference, so leaving them on screen
              // would read as "the move did nothing" — the exact thing an
              // operator checks for.
              //
              // BatchTable's list is plain component state, not react-query, so
              // there is no key to invalidate. `reloadToken` is threaded into its
              // load effect instead: bumping it re-runs the fetch without
              // remounting, which preserves scroll position.
              setSelectedPersonIds([]);
              setMoveOpen(false);
              setReloadToken((t) => t + 1);
              // The KPI tiles ARE react-query: they would keep counting the
              // moved leads until something else invalidated them.
              void queryClient.invalidateQueries({ queryKey: ['batchAggregations', selected.id] });
            }}
          />
        )}
      </div>
    </ConfigProvider>
  );
}
